import { registerSW } from 'virtual:pwa-register';
import { logger } from '../utils/logger';

interface ServiceWorkerEnv {
  PROD: boolean;
}

// How often to ask the browser to re-fetch sw.js and check for a newer
// deployment while the tab stays open. Without this, workbox-window only
// checks once at initial registration, so a tab left open across a deploy
// would never learn about the update until the user manually reloads.
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

/** Callback invoked when a new service worker is waiting and ready to take over. */
export type OnNeedRefreshCallback = (updateSW: (reloadPage?: boolean) => Promise<void>) => void;

export function shouldRegisterServiceWorker(
  env: ServiceWorkerEnv = import.meta.env,
  navigatorRef: Navigator | undefined = typeof window !== 'undefined' ? window.navigator : undefined
): boolean {
  return env.PROD && typeof window !== 'undefined' && Boolean(navigatorRef?.serviceWorker);
}

/**
 * Register the Meats Central service worker.
 *
 * registerType is 'prompt' (set in vite.config.ts) so the new SW stays in
 * "waiting" state instead of activating automatically.  When it is ready,
 * `onNeedRefresh` is called with an `updateSW` function.  Only call
 * `updateSW(true)` AFTER the user has explicitly confirmed they want to reload.
 * This prevents the silent mid-session full-page refresh that 'autoUpdate'
 * + skipWaiting/clientsClaim used to cause.
 */
export function registerProjectMeatsServiceWorker(
  env: ServiceWorkerEnv = import.meta.env,
  navigatorRef: Navigator | undefined = typeof window !== 'undefined'
    ? window.navigator
    : undefined,
  register: typeof registerSW = registerSW,
  onNeedRefresh?: OnNeedRefreshCallback
): ReturnType<typeof registerSW> | null {
  if (!shouldRegisterServiceWorker(env, navigatorRef)) {
    return null;
  }

  // IMPORTANT: vite-plugin-pwa's `onNeedRefresh` callback takes NO arguments —
  // the `updateSW` function is the *return value* of `register(...)` itself,
  // not a parameter passed into `onNeedRefresh`. Previously this code treated
  // `onNeedRefresh(updateSW)` as if `updateSW` were passed in, so it was
  // always `undefined` at runtime. That meant clicking "Refresh" on the
  // banner called `undefined(true)`, throwing and silently failing to ever
  // apply the update — so the waiting service worker never activated and the
  // banner kept reappearing on every reload/poll.
  const updateSW = register({
    immediate: true,
    onNeedRefresh() {
      logger.info('PWA update available — prompting user before reload', {
        component: 'registerServiceWorker',
      });
      if (onNeedRefresh) {
        onNeedRefresh(updateSW);
      }
    },
    onRegisteredSW(swUrl, registration) {
      logger.info('PWA service worker registered', {
        component: 'registerServiceWorker',
        metadata: {
          swUrl,
          scope: registration?.scope ?? 'unknown',
        },
      });

      // Proactively re-check for a newer sw.js on an interval. registration
      // .update() forces the browser to re-fetch sw.js (bypassing HTTP cache
      // per the Service Worker spec's update algorithm) and compare it
      // byte-for-byte against the currently installed worker. Without this,
      // a tab left open for hours/days across a deploy would never surface
      // the "new version available" banner until the user happened to do a
      // full page reload.
      if (registration) {
        setInterval(() => {
          registration.update().catch((error) => {
            logger.warn(
              'PWA periodic update check failed',
              { component: 'registerServiceWorker' },
              error instanceof Error ? error : undefined
            );
          });
        }, UPDATE_CHECK_INTERVAL_MS);
      }
    },
    onOfflineReady() {
      logger.info('PWA app shell ready for offline use', {
        component: 'registerServiceWorker',
      });
    },
    onRegisterError(error) {
      logger.warn(
        'PWA service worker registration failed',
        {
          component: 'registerServiceWorker',
        },
        error
      );
    },
  });

  return updateSW;
}
